/**
 * Les techniciens du planning (lot PL1a, ADR-0015) : on en crée autant qu'il en faut — jamais figés à six —, on corrige
 * la graphie d'un nom, on archive celui qui est parti et on le restaure s'il revient.
 *
 * Un technicien est un NOM de l'annuaire, pas un compte : un sous-traitant (frigoriste, organisme de contrôle) s'y ajoute
 * de la même façon pour que ses visites se planifient comme les autres.
 *
 * Deux limites dites à l'écran plutôt que découvertes à l'usage :
 *  - corriger un nom ne change que sa GRAPHIE (accents, majuscules, espaces) : une autre personne est un autre
 *    technicien, qu'on crée ;
 *  - archiver ne retire rien des visites déjà prévues : elles restent affichées sur une ligne grisée, à reprendre.
 *
 * Chaque geste est enregistré tout de suite (jeton de concurrence `revision`) ; un refus s'affiche en tête du panneau,
 * après relecture de la liste.
 *
 * « Accès au téléphone » (lot T1) : chaque nom se relie à UN compte de l'espace ; le téléphone de ce compte montre alors les
 * interventions de ce technicien. Un compte déjà relié à un autre technicien est grisé (le serveur le refuse aussi, 409).
 * La liste des comptes n'est lue que par qui gère le planning ; les autres voient seulement le nom du compte relié.
 */
import { useCallback, useEffect, useState } from 'react';

import { ApiError, api, type AccesPossible, type Technicien } from '../api/client';
import { changementAcces, libelleAcces, messageRefusAcces, nomDuCompte, optionsAcces, phraseAcces, valeurAcces } from '../domaine/acces-telephone';
import { ErreurFormulaire, Panneau } from './Formulaire';

export function GestionTechniciens({ peutGerer, onChange, onFermer }: { peutGerer: boolean; onChange: () => void; onFermer: () => void }) {
  const [liste, setListe] = useState<readonly Technicien[] | null>(null);
  const [possibles, setPossibles] = useState<readonly AccesPossible[] | null>(null);
  const [erreurComptes, setErreurComptes] = useState<string | null>(null);
  const [erreurAcces, setErreurAcces] = useState<string | null>(null);
  const [erreurLecture, setErreurLecture] = useState<string | null>(null);
  const [nouveau, setNouveau] = useState('');
  const [edition, setEdition] = useState<{ id: string; nom: string } | null>(null);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<ApiError | Error | null>(null);
  const [succes, setSucces] = useState<string | null>(null);

  const charger = useCallback(async () => {
    try {
      const l = await api.techniciensPlanning(true);
      setListe(l.items);
      setErreurLecture(null);
    } catch (e) {
      setErreurLecture(e instanceof ApiError ? e.message : 'Chargement des techniciens impossible.');
    }
    if (!peutGerer) return;
    try {
      const c = await api.accesPossiblesTechniciens();
      setPossibles(c.items);
      setErreurComptes(null);
    } catch (e) {
      setPossibles(null);
      setErreurComptes(e instanceof ApiError ? `Comptes de l’espace illisibles : ${e.message}` : 'Comptes de l’espace illisibles.');
    }
  }, [peutGerer]);

  useEffect(() => {
    void charger();
  }, [charger]);

  /** Un geste : relire la liste PUIS annoncer, pour que le rechargement n'efface pas le message. */
  const agir = async (action: () => Promise<unknown>, phrase: string) => {
    setEnCours(true);
    setErreur(null);
    setErreurAcces(null);
    setSucces(null);
    try {
      await action();
      await charger();
      onChange();
      setSucces(phrase);
      return true;
    } catch (e) {
      await charger();
      setErreur(e instanceof ApiError || e instanceof Error ? e : new Error('Action impossible.'));
      return false;
    } finally {
      setEnCours(false);
    }
  };

  const creer = async () => {
    const nom = nouveau.trim();
    if (nom === '') return;
    if (await agir(() => api.creerTechnicien(nom), `${nom} ajouté.`)) setNouveau('');
  };

  const corriger = async (t: Technicien) => {
    if (!edition || edition.id !== t.id) return;
    const nom = edition.nom.trim();
    if (nom === '' || nom === t.name) {
      setEdition(null);
      return;
    }
    if (await agir(() => api.modifierTechnicien(t.id, { revision: t.revision, label: nom }), `Nom corrigé : ${nom}.`)) setEdition(null);
  };

  /**
   * Relier ou délier un compte. Un refus a son PROPRE message, en français ; il est posé APRÈS la relecture de la liste,
   * pour que le rechargement ne l'efface pas.
   */
  const choisirAcces = async (t: Technicien, valeur: string) => {
    const changement = changementAcces(t, valeur);
    if (changement === null) return;
    const nomCompte = nomDuCompte(valeur, possibles ?? [], t);
    setEnCours(true);
    setErreur(null);
    setErreurAcces(null);
    setSucces(null);
    try {
      await api.modifierTechnicien(t.id, { revision: t.revision, membershipId: changement.membershipId });
      await charger();
      onChange();
      setSucces(phraseAcces(t.name, changement.membershipId === null ? null : nomCompte));
    } catch (e) {
      await charger();
      setErreurAcces(e instanceof ApiError ? messageRefusAcces(e.status, e.message) : messageRefusAcces(0, null));
    } finally {
      setEnCours(false);
    }
  };

  /** La colonne « Accès au téléphone » d'une ligne : une liste pour qui gère, le nom du compte pour les autres. */
  const colonneAcces = (t: Technicien) =>
    peutGerer && possibles !== null ? (
      <label className="liste-techniciens-acces">
        <span className="aide">Accès au téléphone</span>
        <select
          aria-label={`Accès au téléphone de ${t.name}`}
          disabled={enCours}
          value={valeurAcces(t)}
          onChange={(e) => void choisirAcces(t, e.target.value)}
        >
          {optionsAcces(t, possibles, liste ?? []).map((o) => (
            <option key={o.valeur || 'aucun'} value={o.valeur} disabled={o.desactive}>
              {o.libelle}
            </option>
          ))}
        </select>
      </label>
    ) : (
      <span className="liste-techniciens-acces aide">Accès au téléphone : {libelleAcces(t)}</span>
    );

  const actifs = (liste ?? []).filter((t) => !t.archived);
  const archives = (liste ?? []).filter((t) => t.archived);

  return (
    <Panneau
      titre="Techniciens"
      sousTitre="Ceux qui apparaissent dans le planning, une ligne chacun."
      onFermer={onFermer}
      actions={
        <button type="button" className="secondaire" onClick={onFermer}>
          Fermer
        </button>
      }
    >
      {succes && <div className="message succes">{succes}</div>}
      <ErreurFormulaire message={erreur?.message ?? null} details={erreur instanceof ApiError ? erreur.details : undefined} />
      {erreurAcces && <div className="message erreur">{erreurAcces}</div>}
      {erreurLecture && <div className="message erreur">{erreurLecture}</div>}
      {erreurComptes && <div className="message erreur">{erreurComptes}</div>}

      {peutGerer && (
        <div className="champ">
          <label htmlFor="technicien-nouveau">Ajouter un technicien</label>
          <div className="saisie-ligne">
            <input
              id="technicien-nouveau"
              type="text"
              maxLength={100}
              value={nouveau}
              placeholder="Prénom NOM"
              onChange={(e) => setNouveau(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void creer();
              }}
            />
            <button type="button" disabled={enCours || nouveau.trim() === ''} onClick={() => void creer()}>
              Ajouter
            </button>
          </div>
          <p className="aide">Un nom, pas un compte : un sous-traitant s’ajoute de la même façon.</p>
        </div>
      )}

      {liste === null && !erreurLecture && <div className="vide">Chargement…</div>}
      {liste !== null && liste.length === 0 && <div className="vide">Aucun technicien pour l’instant.</div>}

      {actifs.length > 0 && (
        <ul className="liste-techniciens">
          {actifs.map((t) => (
            <li key={t.id}>
              {edition?.id === t.id ? (
                <div className="saisie-ligne">
                  <input
                    type="text"
                    maxLength={100}
                    aria-label={`Nom de ${t.name}`}
                    autoFocus
                    value={edition.nom}
                    onChange={(e) => setEdition({ id: t.id, nom: e.target.value })}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') void corriger(t);
                      if (e.key === 'Escape') {
                        e.preventDefault();
                        setEdition(null);
                      }
                    }}
                  />
                  <button type="button" disabled={enCours} onClick={() => void corriger(t)}>
                    Enregistrer
                  </button>
                  <button type="button" className="secondaire" disabled={enCours} onClick={() => setEdition(null)}>
                    Annuler
                  </button>
                </div>
              ) : (
                <div className="saisie-ligne">
                  <strong className="liste-techniciens-nom">{t.name}</strong>
                  {colonneAcces(t)}
                  {peutGerer && (
                    <>
                      <button type="button" className="secondaire discret" disabled={enCours} onClick={() => setEdition({ id: t.id, nom: t.name })}>
                        Corriger l’orthographe
                      </button>
                      <button
                        type="button"
                        className="secondaire discret"
                        disabled={enCours}
                        onClick={() => {
                          if (
                            window.confirm(
                              `Archiver ${t.name} ? Ses visites déjà prévues restent affichées sur une ligne grisée, à reprendre ; on ne pourra plus lui en affecter.`,
                            )
                          ) {
                            void agir(() => api.modifierTechnicien(t.id, { revision: t.revision, archived: true }), `${t.name} archivé.`);
                          }
                        }}
                      >
                        Archiver
                      </button>
                    </>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {archives.length > 0 && (
        <>
          <p className="aide visite-groupe">Archivés</p>
          <ul className="liste-techniciens">
            {archives.map((t) => (
              <li key={t.id}>
                <div className="saisie-ligne">
                  <span className="liste-techniciens-nom aide">{t.name}</span>
                  {/* Archivé : on peut délier (« Aucun »), jamais relier un nouveau compte (choix grisés). */}
                  {(t.acces || !peutGerer) && colonneAcces(t)}
                  {peutGerer && (
                    <button
                      type="button"
                      className="secondaire discret"
                      disabled={enCours}
                      onClick={() => void agir(() => api.modifierTechnicien(t.id, { revision: t.revision, archived: false }), `${t.name} restauré.`)}
                    >
                      Restaurer
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </>
      )}

      <p className="aide">
        « Corriger l’orthographe » ne change que les accents, les majuscules et les espaces d’un nom : une autre personne est un autre
        technicien, à ajouter.
      </p>
      <p className="aide">
        « Accès au téléphone » : le compte qui verra les interventions de ce technicien sur son téléphone. Un compte ne se relie
        qu’à un seul technicien ; « Aucun » retire l’accès.
      </p>
    </Panneau>
  );
}
